import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ticketsAPI, usersAPI } from '../services/api';
import type { Ticket, TicketStats, CompanyUser, CommentMention, TicketAssignee, AttachmentRef } from '../types';
import { useToast } from '../contexts/ToastContext';
import { serverIsoToMillis, NO_DATE } from '../utils/datetime';
import { detailMessage } from '../utils/errors';
import {
  Ticket as TicketIcon,
  AlertTriangle,
  Clock,
  CheckCircle2,
  MessageSquare,
  User,
  ChevronDown,
  Send,
  X,
  Filter,
  RefreshCw,
  Inbox,
  Loader2,
  ExternalLink,
  Paperclip,
  Lock,
  Mail,
  UserCog,
  MessageSquareReply,
  Globe,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const PRIORITY_STYLES: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const STATUS_STYLES: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  resolved: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  closed: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300',
};

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

function timeAgo(dateStr: string | null | undefined): string {
  const ms = serverIsoToMillis(dateStr);
  if (ms === null) return NO_DATE;
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// Human-readable size for an attachment chip/link (matches the Inbox email
// composer's wording).
function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return '';
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// What the comment composer offers in the file picker. Mirrors the backend's
// ALLOWED_ATTACHMENT_MIME (utils/email_attachments.py). The real gate is the
// server: a rejected upload is a 400 whose `detail` is a plain string, which we
// surface verbatim in a toast.
const COMMENT_FILE_ACCEPT =
  'image/png,image/jpeg,image/webp,image/gif,application/pdf,.pdf,.docx,.pptx,.xlsx,.csv,.txt';

// Where a customer's reply came from (2026-09-16). `source` is empty on staff
// comments and on older customer comments written before the field existed, so
// an unknown value renders nothing rather than guessing a channel.
const COMMENT_SOURCE_LABELS: Record<string, string> = {
  email: 'via email',
  web: 'via ticket page',
};

// One attachment on a comment. Images render as a thumbnail so an emailed photo
// is visible at a glance; everything else keeps the paperclip chip. A dead or
// expired S3 URL falls back to the chip so a broken image can never blow out
// the 375px column.
function CommentAttachment({ att }: { att: AttachmentRef }) {
  const [imageBroken, setImageBroken] = useState(false);
  const size = formatBytes(att.file_size_bytes);
  const title = size ? `${att.filename} (${size})` : att.filename;

  if (att.type === 'image' && !imageBroken) {
    return (
      <a
        href={att.s3_url}
        target="_blank"
        rel="noreferrer"
        title={title}
        className="group block max-w-full min-w-0"
      >
        <img
          src={att.s3_url}
          alt={att.filename}
          loading="lazy"
          onError={() => setImageBroken(true)}
          className="max-h-24 max-w-full w-auto rounded-lg object-cover border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
        />
        <span className="mt-1 flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400 max-w-[160px]">
          <span className="truncate group-hover:underline">{att.filename}</span>
          {size && <span className="shrink-0">{size}</span>}
        </span>
      </a>
    );
  }

  return (
    <a
      href={att.s3_url}
      target="_blank"
      rel="noreferrer"
      title={title}
      className="inline-flex items-center gap-1.5 max-w-full text-xs text-primary hover:underline bg-white/80 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1"
    >
      <Paperclip className="w-3 h-3 shrink-0" />
      <span className="truncate max-w-[140px]">{att.filename}</span>
      {size && <span className="text-gray-400 dark:text-gray-500 shrink-0">{size}</span>}
    </a>
  );
}

// Escape a string for safe use inside a RegExp.
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Render comment text with `@Name` mentions highlighted. `names` is the set
// of known mention display names (resolved from the comment's `mentions`
// array against the team/customer directory). Longest names first so
// "@First Last" wins over a bare "@First". Falls back to plain text when no
// names resolve. Plan-TicketComments-AudienceMentions.
function renderCommentText(text: string, names: string[]): React.ReactNode {
  const resolved = [...new Set(names)].filter(Boolean).sort((a, b) => b.length - a.length);
  if (resolved.length === 0) return text;
  const pattern = new RegExp(`@(?:${resolved.map(escapeRegExp).join('|')})`, 'g');
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <span
        key={`mention-${key++}`}
        className="font-semibold text-primary bg-primary/10 dark:bg-primary/20 rounded px-0.5"
      >
        {m[0]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// ---------------------------------------------------------------------------
// Ticket Detail Slide-Over
// ---------------------------------------------------------------------------
interface TicketDetailProps {
  ticket: Ticket;
  // Everyone in the workspace — @-mention candidates and name lookups.
  teamMembers: CompanyUser[];
  // Only people who can WORK tickets — the assignee dropdown.
  assignees: TicketAssignee[];
  onClose: () => void;
  onUpdated: (updated: Ticket) => void;
}

const TicketDetail: React.FC<TicketDetailProps> = ({ ticket, teamMembers, assignees, onClose, onUpdated }) => {
  const { showToast } = useToast();
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [updatingField, setUpdatingField] = useState<string | null>(null);
  // 2026-09-16 — files now belong to a COMMENT, not to the ticket. Each picked
  // file is uploaded immediately (uploadCommentAttachment) and its ref is held
  // here until the comment posts, so the file follows that comment's audience:
  // emailed to the customer on a reply, team-only on an internal note.
  const [pendingAttachments, setPendingAttachments] = useState<AttachmentRef[]>([]);
  // In-flight uploads, one entry per file, so each shows its own spinner.
  const [uploadingFiles, setUploadingFiles] = useState<{ id: string; name: string }[]>([]);
  const commentFileRef = useRef<HTMLInputElement>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  // Plan-TicketComments-AudienceMentions — comment audience. Default
  // "customer" (Reply to customer) per the locked decision; flipping to
  // "internal" hides the comment from the public status page and sends no
  // customer email.
  const [isInternal, setIsInternal] = useState(false);

  // Resolved mentions accumulated as the user picks from the @-autocomplete.
  // Carried to the BE as a structured array; the text keeps the literal
  // `@Name` for display. De-duped on insert.
  const [mentions, setMentions] = useState<CommentMention[]>([]);

  // @-mention autocomplete UI state. `query` is the text typed after the
  // active `@`; `anchor` is the index of that `@` in commentText so we can
  // splice the chosen name in. null query = dropdown closed.
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionAnchor, setMentionAnchor] = useState<number | null>(null);

  // Mentionable directory: the customer (if named) + ACTIVE team members.
  // `is_active` is undefined for workspace members (implicitly active) — so
  // treat undefined as active (only an explicit `false` excludes).
  const mentionCandidates = React.useMemo(() => {
    const list: { key: string; name: string; mention: CommentMention }[] = [];
    if (ticket.customer_name) {
      list.push({
        key: 'customer',
        name: ticket.customer_name,
        mention: { type: 'customer' },
      });
    }
    for (const m of teamMembers) {
      if (m.is_active === false) continue;
      list.push({
        key: `staff:${m.id}`,
        name: m.full_name,
        mention: { type: 'staff', user_id: m.id },
      });
    }
    return list;
  }, [teamMembers, ticket.customer_name]);

  const mentionMatches = React.useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    return mentionCandidates
      .filter((c) => c.name.toLowerCase().includes(q))
      .slice(0, 6);
  }, [mentionQuery, mentionCandidates]);

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket.comments]);

  // Detect an in-progress `@mention` token immediately before the caret:
  // an `@` that is at start-of-text or preceded by whitespace, followed by
  // word-ish chars (allow spaces inside names via a non-greedy lookahead is
  // overkill — we match the simple `@word` token up to the caret).
  const updateMentionState = (value: string, caret: number) => {
    const upto = value.slice(0, caret);
    const m = /(?:^|\s)@([\w-]*)$/.exec(upto);
    if (m) {
      setMentionQuery(m[1]);
      setMentionAnchor(caret - m[1].length - 1); // index of the '@'
    } else {
      setMentionQuery(null);
      setMentionAnchor(null);
    }
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setCommentText(value);
    updateMentionState(value, e.target.selectionStart ?? value.length);
  };

  const selectMention = (cand: { name: string; mention: CommentMention }) => {
    if (mentionAnchor === null) return;
    // Replace from the '@' anchor through the current query length with
    // `@Name ` (trailing space so the next word starts a fresh token).
    const before = commentText.slice(0, mentionAnchor);
    const after = commentText.slice(
      mentionAnchor + 1 + (mentionQuery?.length ?? 0),
    );
    const inserted = `@${cand.name} `;
    const next = before + inserted + after;
    setCommentText(next);
    setMentions((prev) => {
      // De-dupe: customer once; staff once per user_id.
      const exists = prev.some(
        (p) =>
          p.type === cand.mention.type &&
          (p.type === 'customer' || p.user_id === cand.mention.user_id),
      );
      return exists ? prev : [...prev, cand.mention];
    });
    setMentionQuery(null);
    setMentionAnchor(null);
    // Restore focus + caret just after the inserted name.
    requestAnimationFrame(() => {
      const el = commentRef.current;
      if (el) {
        const pos = before.length + inserted.length;
        el.focus();
        el.setSelectionRange(pos, pos);
      }
    });
  };

  const handleUpdate = async (field: string, value: string) => {
    setUpdatingField(field);
    try {
      const updated = await ticketsAPI.update(ticket.ticket_id, { [field]: value });
      onUpdated(updated.data);
    } catch (err: any) {
      showToast(detailMessage(err.response?.data?.detail, 'Failed to update ticket field.'), 'error');
    } finally {
      setUpdatingField(null);
    }
  };

  const handleAddComment = async () => {
    // A file with no words is still a message ("here's the invoice"), so an
    // attachment on its own is enough to send.
    if (!commentText.trim() && pendingAttachments.length === 0) return;
    setSubmittingComment(true);
    try {
      const text = commentText.trim();
      // Only send mentions whose `@Name` literal still survives in the text
      // (the user may have backspaced one out after selecting it).
      const liveMentions = mentions.filter((mn) => {
        const name =
          mn.type === 'customer'
            ? ticket.customer_name
            : teamMembers.find((m) => m.id === mn.user_id)?.full_name;
        return !!name && text.includes(`@${name}`);
      });
      const updated = await ticketsAPI.addComment(ticket.ticket_id, text, {
        is_internal: isInternal,
        mentions: liveMentions,
        attachments: pendingAttachments,
      });
      onUpdated(updated.data);
      setCommentText('');
      setPendingAttachments([]);
      setMentions([]);
      setMentionQuery(null);
      setMentionAnchor(null);
      // Audience intentionally PERSISTS across sends (staff often post a
      // run of internal notes or a run of customer replies).
    } catch (err: any) {
      showToast(detailMessage(err.response?.data?.detail, 'Failed to add comment.'), 'error');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Stage files for the next comment. Each uploads on its own so one rejection
  // (bad type / too big → 400 with a string `detail`) never drops the others.
  const handleCommentFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset the input straight away so re-picking the SAME file still fires.
    e.target.value = '';
    if (files.length === 0) return;
    await Promise.all(
      files.map(async (file) => {
        const id = `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`;
        setUploadingFiles((prev) => [...prev, { id, name: file.name }]);
        try {
          const res = await ticketsAPI.uploadCommentAttachment(ticket.ticket_id, file);
          setPendingAttachments((prev) => [...prev, res.data]);
        } catch (err: any) {
          showToast(
            detailMessage(err.response?.data?.detail, `Couldn't attach ${file.name}.`),
            'error',
          );
        } finally {
          setUploadingFiles((prev) => prev.filter((f) => f.id !== id));
        }
      }),
    );
  };

  const removePendingAttachment = (idx: number) =>
    setPendingAttachments((prev) => prev.filter((_, i) => i !== idx));

  const navigate = useNavigate();

  const handleNavigateToConversation = () => {
    if (!ticket.conversation_id) return;

    // We determine if it's an email or chat by checking if it looks like an email thread ID (usually MongoDB ObjectId) vs a session ID (often starting with sess_)
    // Let's pass the conversation ID as a query param to the inbox, and let the inbox try to open it.
    // Assuming conversation_id is email thread id if it doesn't start with "sess_" (which is our standard for widget chats)
    const isEmail = !ticket.conversation_id.startsWith('sess_');
    const tab = isEmail ? 'emails' : 'chats';
    navigate(`/inbox?tab=${tab}&id=${ticket.conversation_id}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-gray-900 shadow-2xl flex flex-col h-full overflow-hidden animate-slide-in-right">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100 dark:border-gray-800">
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center space-x-2 mb-1 flex-wrap gap-y-1">
              <span className="text-xs font-mono text-gray-400">#{ticket.ticket_id}</span>
              {ticket.sla_breached && (
                <span className="flex items-center space-x-1 text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-2 py-0.5 rounded-full font-medium">
                  <AlertTriangle className="w-3 h-3" />
                  <span>SLA Breached</span>
                </span>
              )}
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{ticket.title}</h2>
            {ticket.agent_name && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Agent: {ticket.agent_name}</p>
            )}
            {ticket.conversation_id && (
              <button
                onClick={handleNavigateToConversation}
                className="mt-2 flex items-center space-x-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span>View Original Conversation</span>
              </button>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Controls row */}
          <div className="p-6 grid grid-cols-3 gap-3 border-b border-gray-100 dark:border-gray-800">
            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
              <div className="relative">
                <select
                  value={ticket.status}
                  onChange={(e) => handleUpdate('status', e.target.value)}
                  disabled={updatingField === 'status'}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary/50 outline-none appearance-none pr-7"
                >
                  {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                {updatingField === 'status' ? <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-gray-400" /> : <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />}
              </div>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Priority</label>
              <div className="relative">
                <select
                  value={ticket.priority}
                  onChange={(e) => handleUpdate('priority', e.target.value)}
                  disabled={updatingField === 'priority'}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary/50 outline-none appearance-none pr-7"
                >
                  {Object.entries(PRIORITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                {updatingField === 'priority' ? <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-gray-400" /> : <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />}
              </div>
            </div>

            {/* Assignee */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Assigned To</label>
              <div className="relative">
                <select
                  value={ticket.assigned_to || ''}
                  onChange={(e) => handleUpdate('assigned_to', e.target.value)}
                  disabled={updatingField === 'assigned_to'}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary/50 outline-none appearance-none pr-7"
                >
                  <option value="">Unassigned</option>
                  {/* Only people who can WORK tickets (backend: utils/ticket_assignees.py) —
                      owner, Admin, Tickets Manager, or a custom role with full ticket CRUD.
                      This used to list every team member, Viewers included. */}
                  {assignees.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.full_name}
                      {a.is_owner ? ' (Owner)' : a.role_names.length ? ` (${a.role_names.join(', ')})` : ''}
                    </option>
                  ))}
                  {/* A current assignee who no longer qualifies stays visible (disabled) so the
                      select never renders a blank value. */}
                  {ticket.assigned_to && !assignees.some(a => a.id === ticket.assigned_to) && (
                    <option value={ticket.assigned_to} disabled>
                      {ticket.assigned_to_name || 'Current assignee'} (can't be assigned tickets)
                    </option>
                  )}
                </select>
                {updatingField === 'assigned_to' ? <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-gray-400" /> : <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="p-6 border-b border-gray-100 dark:border-gray-800">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Description</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">{ticket.description}</p>
          </div>

          {/* Attachments */}
          {(() => {
            const allAttachments = [...(ticket.attachments || []), ...(ticket.ticket_attachments || [])];
            return (
              <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                {/* Read-only history (2026-09-16). Files used to be uploaded HERE,
                    which made them staff-only — a customer could never see one.
                    New files go on a COMMENT instead; this panel keeps whatever
                    already arrived from the ticket or the original chat. */}
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center space-x-2 mb-1">
                  <Paperclip className="w-4 h-4 text-primary" />
                  <span>Attachments ({allAttachments.length})</span>
                </h3>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3 leading-snug">
                  Internal — customers can't see these. To send a file to the customer, attach it to a reply below.
                </p>
                {allAttachments.length === 0 ? (
                  <p className="text-xs text-gray-400 dark:text-gray-500">No attachments yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {allAttachments.map((att, i) =>
                      att.type === 'image' ? (
                        <img
                          key={i}
                          src={att.s3_url}
                          alt={att.filename}
                          className="rounded-lg cursor-pointer border border-gray-200 dark:border-gray-700 object-cover"
                          style={{ maxWidth: '120px', maxHeight: '90px' }}
                          onClick={() => window.open(att.s3_url, '_blank')}
                        />
                      ) : (
                        <a
                          key={i}
                          href={att.s3_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs text-primary hover:underline bg-gray-50 dark:bg-gray-800 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700"
                        >
                          <Paperclip className="w-3 h-3" />
                          <span className="max-w-[120px] truncate">{att.filename}</span>
                        </a>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Customer Info */}
          {(ticket.customer_name || ticket.customer_email) && (
            <div className="p-6 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Customer</h3>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white text-xs font-semibold">
                  {ticket.customer_name?.charAt(0).toUpperCase() || '?'}
                </div>
                {/* The contact deep-link keys on the email, so only link when we
                    have one; otherwise the name stays plain text. Contacts.tsx
                    selects the match, or offers to create it prefilled. */}
                {ticket.customer_email ? (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/contacts?email=${encodeURIComponent(ticket.customer_email || '')}&name=${encodeURIComponent(ticket.customer_name || '')}`,
                      )
                    }
                    className="min-w-0 text-left group"
                    title="Open this contact"
                  >
                    {ticket.customer_name && (
                      <p className="text-sm font-medium text-primary group-hover:underline truncate">{ticket.customer_name}</p>
                    )}
                    <p className="text-xs text-primary/80 group-hover:underline truncate">{ticket.customer_email}</p>
                  </button>
                ) : (
                  <div className="min-w-0">
                    {ticket.customer_name && <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{ticket.customer_name}</p>}
                  </div>
                )}
                {/* Kept separate so the name/email click goes to the contact
                    record while emailing them is still one tap. Goes to the
                    Inbox composer — NOT `mailto:`, which threw the agent out to
                    a desktop mail app and left no record on the thread. The
                    ticket's own agent rides along so the mail goes out from the
                    right mailbox without the user picking one. */}
                {ticket.customer_email && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/inbox?tab=emails&action=compose&email=${encodeURIComponent(ticket.customer_email || '')}`
                        + (ticket.agent_id ? `&agent=${encodeURIComponent(ticket.agent_id)}` : ''),
                      )
                    }
                    className="ml-auto shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
                    title={`Email ${ticket.customer_email} from the Inbox`}
                    aria-label={`Email ${ticket.customer_email} from the Inbox`}
                  >
                    <Mail className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Comments */}
          <div className="p-6">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center space-x-2">
              <MessageSquare className="w-4 h-4 text-primary" />
              <span>Comments ({ticket.comments.length})</span>
            </h3>

            {ticket.comments.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">No comments yet. Be the first to add one.</p>
            ) : (
              <div className="space-y-4">
                {ticket.comments.map((comment) => {
                  // Three kinds: a customer's reply (by email or the public
                  // /tickets/track page — `author_type === 'customer'`), an
                  // internal note (team-only), or a customer-facing staff
                  // comment (emailed + shown on the public page). The backend
                  // now reports legacy flagless staff comments as
                  // `is_internal: true`, so only an explicit false reads as
                  // customer-facing. Customer is checked first so a reply is
                  // never mislabelled "Emailed customer".
                  const fromCustomer = comment.author_type === 'customer';
                  const internal = !fromCustomer && comment.is_internal === true;
                  // Only a customer reply carries a channel; staff comments and
                  // pre-2026-09-16 customer comments have an empty/absent
                  // `source`, and then no channel line is shown at all.
                  const channelLabel = fromCustomer
                    ? COMMENT_SOURCE_LABELS[comment.source ?? ''] ?? ''
                    : '';
                  // Resolve this comment's mention display names so they can
                  // be highlighted in the text.
                  const mentionNames = (comment.mentions ?? [])
                    .map((mn) =>
                      mn.type === 'customer'
                        ? ticket.customer_name
                        : teamMembers.find((m) => m.id === mn.user_id)?.full_name,
                    )
                    .filter((n): n is string => !!n);
                  return (
                    <div key={comment.comment_id} className="flex space-x-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 ${
                          fromCustomer
                            ? 'bg-gradient-to-br from-sky-500 to-blue-600'
                            : internal
                              ? 'bg-gradient-to-br from-amber-500 to-orange-500'
                              : 'bg-gradient-to-br from-primary to-purple-600'
                        }`}
                      >
                        {comment.author_name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div
                        className={`flex-1 min-w-0 rounded-xl p-3 border ${
                          fromCustomer
                            ? 'bg-sky-50 dark:bg-sky-900/15 border-sky-200 dark:border-sky-900/40'
                            : internal
                              ? 'bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-900/40'
                              : 'bg-gray-50 dark:bg-gray-800/60 border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center flex-wrap gap-x-2 gap-y-1 min-w-0">
                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 truncate">{comment.author_name || 'Customer'}</span>
                            {fromCustomer ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-sky-700 dark:text-sky-300 bg-sky-100 dark:bg-sky-900/40 px-1.5 py-0.5 rounded-full shrink-0">
                                <MessageSquareReply className="w-2.5 h-2.5" /> Customer reply
                              </span>
                            ) : internal ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded-full">
                                <Lock className="w-2.5 h-2.5" /> Internal
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/40 px-1.5 py-0.5 rounded-full">
                                <Mail className="w-2.5 h-2.5" /> Emailed customer
                              </span>
                            )}
                            {channelLabel && (
                              <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400 shrink-0">
                                {comment.source === 'email' ? (
                                  <Mail className="w-2.5 h-2.5" aria-hidden="true" />
                                ) : (
                                  <Globe className="w-2.5 h-2.5" aria-hidden="true" />
                                )}
                                {channelLabel}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-gray-400 shrink-0">{timeAgo(comment.created_at)}</span>
                        </div>
                        {comment.text && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                            {renderCommentText(comment.text, mentionNames)}
                          </p>
                        )}
                        {(comment.attachments?.length ?? 0) > 0 && (
                          <div className="mt-2 flex flex-wrap items-start gap-1.5">
                            {comment.attachments!.map((att, i) => (
                              <CommentAttachment key={att.s3_key || `${att.filename}-${i}`} att={att} />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={commentsEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Comment input — Plan-TicketComments-AudienceMentions */}
        <div
          className={`p-4 border-t bg-white dark:bg-gray-900 transition-colors ${
            isInternal
              ? 'border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/10'
              : 'border-gray-100 dark:border-gray-800'
          }`}
        >
          {/* Audience selector + hint */}
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="relative">
              <select
                value={isInternal ? 'internal' : 'customer'}
                onChange={(e) => setIsInternal(e.target.value === 'internal')}
                className={`text-xs font-semibold pl-2.5 pr-7 py-1.5 rounded-lg border outline-none appearance-none cursor-pointer transition-colors ${
                  isInternal
                    ? 'border-amber-300 dark:border-amber-700 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                    : 'border-green-300 dark:border-green-800 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                }`}
              >
                <option value="customer">Reply to customer</option>
                <option value="internal">Internal note</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none opacity-60" />
            </div>
            <span
              className={`flex items-center gap-1 text-[11px] font-medium ${
                isInternal ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'
              }`}
            >
              {isInternal ? (
                <><Lock className="w-3 h-3" /> Internal — not visible to the customer</>
              ) : (
                <><Mail className="w-3 h-3" /> Will email the customer</>
              )}
            </span>
          </div>

          {/* Files staged for THIS comment. They post with the comment and then
              follow its audience; nothing is attached to the ticket itself. */}
          {(pendingAttachments.length > 0 || uploadingFiles.length > 0) && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {pendingAttachments.map((att, i) => (
                <span
                  key={att.s3_key || `${att.filename}-${i}`}
                  className="inline-flex items-center gap-1.5 max-w-full text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg pl-2 pr-1 py-1"
                >
                  <Paperclip className="w-3 h-3 shrink-0 text-primary" />
                  <span className="truncate max-w-[120px]">{att.filename}</span>
                  <span className="text-gray-400 dark:text-gray-500 shrink-0">{formatBytes(att.file_size_bytes)}</span>
                  <button
                    type="button"
                    onClick={() => removePendingAttachment(i)}
                    className="p-0.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors shrink-0"
                    aria-label={`Remove ${att.filename}`}
                    title={`Remove ${att.filename}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {uploadingFiles.map((f) => (
                <span
                  key={f.id}
                  className="inline-flex items-center gap-1.5 max-w-full text-xs bg-white dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 rounded-lg px-2 py-1"
                >
                  <Loader2 className="w-3 h-3 shrink-0 animate-spin text-primary" />
                  <span className="truncate max-w-[120px]">{f.name}</span>
                </span>
              ))}
            </div>
          )}

          <div className="relative flex items-end gap-2">
            {/* @-mention autocomplete dropdown — opens above the composer */}
            {mentionQuery !== null && mentionMatches.length > 0 && (
              <div className="absolute bottom-full left-0 mb-1 w-64 max-h-56 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl z-10 py-1">
                {mentionMatches.map((cand) => (
                  <button
                    key={cand.key}
                    type="button"
                    onMouseDown={(e) => {
                      // onMouseDown (not onClick) so the textarea doesn't blur
                      // and lose its caret before we splice the name in.
                      e.preventDefault();
                      selectMention(cand);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
                  >
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-semibold shrink-0 ${
                        cand.mention.type === 'customer'
                          ? 'bg-gradient-to-br from-gray-400 to-gray-500'
                          : 'bg-gradient-to-br from-primary to-purple-600'
                      }`}
                    >
                      {cand.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-gray-800 dark:text-gray-200 truncate">{cand.name}</span>
                      <span className="block text-[10px] text-gray-400 flex items-center gap-1">
                        {cand.mention.type === 'customer'
                          ? <><User className="w-2.5 h-2.5" /> Customer</>
                          : <><UserCog className="w-2.5 h-2.5" /> Team member</>}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            <textarea
              ref={commentRef}
              value={commentText}
              onChange={handleCommentChange}
              onKeyDown={(e) => {
                // Let the autocomplete swallow Escape; otherwise Ctrl/Cmd+Enter submits.
                if (e.key === 'Escape' && mentionQuery !== null) {
                  setMentionQuery(null);
                  setMentionAnchor(null);
                  return;
                }
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddComment();
              }}
              placeholder="Add a comment… type @ to mention. (Ctrl+Enter to submit)"
              rows={2}
              className="flex-1 text-sm px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all resize-none"
            />
            <input
              ref={commentFileRef}
              type="file"
              multiple
              accept={COMMENT_FILE_ACCEPT}
              className="hidden"
              onChange={handleCommentFiles}
            />
            <button
              type="button"
              onClick={() => commentFileRef.current?.click()}
              disabled={submittingComment}
              className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-primary hover:border-primary/50 transition-colors disabled:opacity-50 flex-shrink-0"
              title="Attach files to this comment"
              aria-label="Attach files to this comment"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <button
              onClick={handleAddComment}
              disabled={
                (!commentText.trim() && pendingAttachments.length === 0) ||
                submittingComment ||
                uploadingFiles.length > 0
              }
              className="px-4 py-2.5 bg-gradient-to-r from-primary to-purple-600 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:scale-100 flex-shrink-0"
            >
              {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>

          {/* Where a file ends up depends on the audience above — say so, and
              spell out what each audience means. */}
          <p
            className={`mt-2 flex items-center gap-1.5 text-[11px] font-medium ${
              isInternal ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'
            }`}
          >
            {isInternal ? (
              <><Lock className="w-3 h-3 shrink-0" /> Files stay with the team.</>
            ) : (
              <><Mail className="w-3 h-3 shrink-0" /> Files will be emailed to the customer.</>
            )}
          </p>
          <p className="mt-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
            A reply to the customer is emailed to them, attached files included. An internal note never leaves the team.
          </p>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Tickets Page
// ---------------------------------------------------------------------------
const Tickets: React.FC = () => {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [teamMembers, setTeamMembers] = useState<CompanyUser[]>([]);
  const [assignees, setAssignees] = useState<TicketAssignee[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Deep-link: auto-open a specific ticket by ID from ?ticket= param
  const pendingTicketId = searchParams.get('ticket') || '';

  const fetchAll = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [ticketsRes, statsRes, membersRes, assigneesRes] = await Promise.all([
        ticketsAPI.list(statusFilter || undefined, priorityFilter || undefined),
        ticketsAPI.getStats(),
        usersAPI.list(),
        // A failure here must not blank the whole page — the dropdown just
        // offers "Unassigned" until the next refresh.
        ticketsAPI.getAssignees().catch(() => ({ data: [] as TicketAssignee[] })),
      ]);
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
      setTeamMembers(membersRes.data);
      setAssignees(assigneesRes.data);
    } catch (err: any) {
      showToast(detailMessage(err.response?.data?.detail, 'Failed to load tickets.'), 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchAll(); }, [statusFilter, priorityFilter]);

  // Auto-open ticket from deep-link once tickets are loaded
  useEffect(() => {
    if (!loading && pendingTicketId && tickets.length > 0) {
      const match = tickets.find(t => t.ticket_id === pendingTicketId);
      if (match) {
        setSelectedTicket(match);
        searchParams.delete('ticket');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [loading, tickets, pendingTicketId]);

  const handleTicketUpdated = (updated: Ticket) => {
    setTickets(prev => prev.map(t => t.ticket_id === updated.ticket_id ? updated : t));
    if (selectedTicket?.ticket_id === updated.ticket_id) {
      setSelectedTicket(updated);
    }
  };

  // -- Live refresh on notification SSE -----------------------------------
  // Layout.tsx runs the notification EventSource and re-broadcasts each
  // notification as a window `app:notification` CustomEvent. When a
  // ticket-related one arrives (new comment, mention, assignment, etc.) we
  // refetch the list + stats so new comments appear WITHOUT a manual page
  // refresh, and update the open modal in place. The list endpoint returns
  // full ticket docs incl. `comments[]` (same `_map_ticket_doc` mapper as
  // get-single), so the modal's thread updates straight from the refreshed
  // list — no get-single endpoint needed.
  //
  // The current filters are read off refs so the always-mounted listener
  // never re-subscribes (and never goes stale) when filters change.
  const filtersRef = useRef({ statusFilter, priorityFilter });
  useEffect(() => {
    filtersRef.current = { statusFilter, priorityFilter };
  }, [statusFilter, priorityFilter]);

  useEffect(() => {
    let debounce: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const refetchSilently = async () => {
      const { statusFilter: sf, priorityFilter: pf } = filtersRef.current;
      try {
        const [ticketsRes, statsRes] = await Promise.all([
          ticketsAPI.list(sf || undefined, pf || undefined),
          ticketsAPI.getStats(),
        ]);
        if (cancelled) return;
        const fresh = ticketsRes.data;
        setTickets(fresh);
        setStats(statsRes.data);
        // Refresh the open modal in place (mirror handleTicketUpdated). If
        // the ticket fell out of the filtered list (e.g. status changed),
        // leave the modal as-is rather than closing it under the user.
        setSelectedTicket((cur) => {
          if (!cur) return cur;
          const match = fresh.find((t) => t.ticket_id === cur.ticket_id);
          return match ?? cur;
        });
      } catch {
        // Silent — a transient refetch failure just leaves the prior data;
        // the user can still hit the manual Refresh button.
      }
    };

    const handler = (e: Event) => {
      // Resilient: a missing/odd detail must never throw.
      const detail = (e as CustomEvent).detail;
      const type: string = (detail && typeof detail.type === 'string' && detail.type) || '';
      const hasTicketMeta = !!(detail && detail.metadata && detail.metadata.ticket_id);
      if (!type.startsWith('ticket') && !hasTicketMeta) return;
      // Coalesce a burst of events into a single refetch (~1s).
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(refetchSilently, 1000);
    };

    window.addEventListener('app:notification', handler);
    return () => {
      cancelled = true;
      if (debounce) clearTimeout(debounce);
      window.removeEventListener('app:notification', handler);
    };
  }, []);

  const statCards = stats ? [
    { label: 'Open', value: stats.open_tickets, icon: Inbox, color: 'from-blue-500 to-blue-600', filter: 'open' },
    { label: 'In Progress', value: stats.in_progress_tickets, icon: Clock, color: 'from-amber-500 to-amber-600', filter: 'in_progress' },
    { label: 'Resolved Today', value: stats.resolved_today, icon: CheckCircle2, color: 'from-green-500 to-green-600', filter: 'resolved' },
    { label: 'SLA Breached', value: stats.sla_breached, icon: AlertTriangle, color: 'from-red-500 to-red-600', filter: '' },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
            Support Tickets
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage and collaborate on customer support tickets.</p>
        </div>
        <button
          onClick={() => fetchAll(true)}
          disabled={refreshing}
          className="flex items-center space-x-2 px-4 py-2 glass rounded-xl text-gray-700 dark:text-gray-300 hover:scale-105 transition-all"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium">Refresh</span>
        </button>
      </div>

      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {statCards.map((card) => {
            const Icon = card.icon;
            return (
              <button
                key={card.label}
                onClick={() => setStatusFilter(prev => prev === card.filter ? '' : card.filter)}
                className={`glass rounded-2xl p-4 flex items-center space-x-3 text-left transition-all hover:scale-105 ${statusFilter === card.filter && card.filter ? 'ring-2 ring-primary' : ''}`}
              >
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{card.value}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{card.label}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="glass rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Filter:</span>

        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm pl-3 pr-8 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary/50 outline-none appearance-none"
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
        </div>

        <div className="relative">
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="text-sm pl-3 pr-8 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary/50 outline-none appearance-none"
          >
            <option value="">All Priorities</option>
            {Object.entries(PRIORITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
        </div>

        {(statusFilter || priorityFilter) && (
          <button
            onClick={() => { setStatusFilter(''); setPriorityFilter(''); }}
            className="flex items-center space-x-1 text-sm text-red-500 hover:text-red-700 transition-colors"
          >
            <X className="w-3 h-3" />
            <span>Clear</span>
          </button>
        )}

        <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">{tickets.length} ticket{tickets.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Ticket List */}
      <div className="glass rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" />
            <p className="mt-3 text-gray-500 dark:text-gray-400">Loading tickets...</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="p-12 text-center">
            <TicketIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No tickets found.</p>
            {(statusFilter || priorityFilter) && (
              <button onClick={() => { setStatusFilter(''); setPriorityFilter(''); }} className="mt-2 text-sm text-primary hover:underline">
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {tickets.map((ticket) => (
              <button
                key={ticket.ticket_id}
                onClick={() => setSelectedTicket(ticket)}
                className="w-full text-left flex items-start p-5 hover:bg-white/5 transition-colors group"
              >
                {/* Priority indicator */}
                <div className={`w-1 self-stretch rounded-full mr-4 flex-shrink-0 ${ticket.priority === 'urgent' ? 'bg-red-500' :
                  ticket.priority === 'high' ? 'bg-orange-500' :
                    ticket.priority === 'medium' ? 'bg-blue-500' : 'bg-gray-300'
                  }`} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center space-x-2 flex-wrap gap-y-1 mb-1">
                        <span className="text-xs font-mono text-gray-400">#{ticket.ticket_id}</span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[ticket.status]}`}>
                          {STATUS_LABELS[ticket.status]}
                        </span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${PRIORITY_STYLES[ticket.priority]}`}>
                          {PRIORITY_LABELS[ticket.priority]}
                        </span>
                        {ticket.sla_breached && (
                          <span className="flex items-center space-x-1 text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-2 py-0.5 rounded-full">
                            <AlertTriangle className="w-3 h-3" />
                            <span>SLA</span>
                          </span>
                        )}
                      </div>
                      <p className="font-semibold text-gray-900 dark:text-white truncate group-hover:text-primary transition-colors">
                        {ticket.title}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">{ticket.description}</p>
                    </div>

                    <div className="flex-shrink-0 text-right">
                      <p className="text-xs text-gray-400">{timeAgo(ticket.created_at)}</p>
                      {ticket.assigned_to_name && (
                        <div className="flex items-center justify-end space-x-1 mt-1">
                          <User className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500 dark:text-gray-400">{ticket.assigned_to_name}</span>
                        </div>
                      )}
                      {ticket.comments.length > 0 && (
                        <div className="flex items-center justify-end space-x-1 mt-1">
                          <MessageSquare className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">{ticket.comments.length}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail Panel */}
      {selectedTicket && (
        <TicketDetail
          ticket={selectedTicket}
          teamMembers={teamMembers}
          assignees={assignees}
          onClose={() => setSelectedTicket(null)}
          onUpdated={handleTicketUpdated}
        />
      )}
    </div>
  );
};

export default Tickets;
